import { z } from 'zod';
import { BADGE_FONT_WEIGHTS, BADGE_ORIENTATIONS, BADGE_SLOT_FIELDS } from './badge-layout.js';
/**
 * The ink a `muted` text item is drawn in — the field-name captions above each value.
 *
 * THIS IS A SANCTIONED RAW COLOUR, not a missing token. The badge is a 1-bit raster, not a
 * themed surface; it already carries literal `#ffffff`/`#000000` for the same reason, and
 * `apps/visitor/src/styles/print.css` documents the same carve-out for print.
 *
 * WHY `#595959` AND NOT `#808080`. Both rasterizers threshold with luminance `(r+g+b)/3` and
 * ink `< 128` (`apps/kiosk/lib/core/printing/badge-raster.dart` `_thresholdLuminance`, and
 * its transcription in `apps/api/scripts/badge-fixture-pipeline.ts`). A neutral grey's
 * luminance IS its channel value, so `#808080` is 128 — exactly ON the cut, and `< 128` is
 * false, so a mid-grey caption prints as NOTHING. `#595959` is 0x59 = 89: 39 below the cut,
 * and 30% clear of it even before antialiasing. An antialiased edge pixel at coverage `a`
 * composites to `255 - a*(255-89)`, which crosses the cut at `a > 0.765`, so a caption
 * prints slightly lighter-weight than a black one and never disappears.
 *
 * The caption's visual hierarchy must NOT rest on this value — every grey pixel that
 * survives is solid black on the badge. Size, uppercase and letter-spacing carry it; the
 * grey is for the admin's on-screen preview only.
 */
export const BADGE_MUTED_INK = '#595959';
/**
 * ADR-0024: the server resolves template + preset into this display list; the kiosk only
 * draws it. The vocabulary below is deliberately small (ADR-0024 §Consequences, "Bad") — a
 * `badgeHeader` primitive would defeat the decision by moving layout back onto the kiosk; a
 * `rect` plus a `reversed` `text` already expresses a knockout header block. Adding a `kind`
 * is the one change here that costs a kiosk release; everything else is data.
 */
/** Bumped only when a drawer written against the previous version could MISREAD a list —
 *  never for a new primitive kind, which an older drawer already skips safely. */
export const BADGE_DISPLAY_LIST_VERSION = 1;
/** U+2026. Exported so both renderers truncate to the identical glyph. */
export const BADGE_TEXT_ELLIPSIS = '…';
/** Roboto's average glyph advance as a fraction of the font size. An ESTIMATE, shared by both
 *  implementations for truncation only — NOT a measurement. Both must use this exact ratio or
 *  they cut the same string at different characters and every long-name fixture fails. */
export const BADGE_TEXT_ADVANCE_RATIO = 0.52;
/** What a fill means, never what colour it is. BLACK -> ink. WHITE -> no ink. ACCENT -> the
 *  template's accent colour, which a mono printer thresholds to ink. The drawer resolves it;
 *  the list never carries a literal colour. */
export const BADGE_FILL_ROLES = ['BLACK', 'WHITE', 'ACCENT'];
export const BADGE_TEXT_ALIGNS = ['LEFT', 'CENTER', 'RIGHT'];
/** Bytes the drawer already holds locally. Never a file id and never a URL. */
export const BADGE_IMAGE_SOURCES = ['PHOTO', 'LOGO'];
/** How a source image fills a box whose aspect ratio may not match the image's own.
 *  `CONTAIN`: scaled to fit entirely inside the box, centred, letterboxed — the box's own
 *  background (or whatever painted under it) shows on the sides that don't fill. Use for a LOGO:
 *  a brand mark must never be cropped.
 *  `COVER`: scaled to fill the whole box, centre-cropped — no letterbox, no dead band. Use for a
 *  PHOTO: a fixed photo column should never show background either side of a visitor's face.
 *  Set once, server-side, by buildBadgeDisplayList — never inferred from `source` by a drawer
 *  (docs/adr/0024-the-badge-layout-is-data.md: "any layout change is a server-side change"). */
export const BADGE_IMAGE_FIT_MODES = ['CONTAIN', 'COVER'];
/** RND-05: the QR carries an opaque visit identifier and nothing else. */
export const BADGE_QR_PAYLOAD_KINDS = ['VISIT_ID'];
/** Exactly five primitives. Adding a sixth is the one change here that forces a kiosk
 *  release — see the module doc comment above. An unknown `kind` is a closed-union rejection
 *  on the server (this file validates its own output strictly), but a *drawer* (the Dart
 *  kiosk, S35-TASK-044) must skip an item whose `kind` it does not recognize and draw the
 *  rest, so an older kiosk meeting a newer list prints a badge missing one element rather
 *  than stranding the visitor. */
export const BADGE_PRIMITIVE_KINDS = ['rect', 'text', 'image', 'qr', 'rule'];
/**
 * Every item carries these two. `flow` names the reflow group it belongs to (null = none);
 * `collapseHeightDots` is how far later members of that group move up when THIS item is
 * dropped for want of content.
 *
 * The reflow rule, implemented identically by both drawers from this one statement:
 *   1. A list is positioned for the case where every item has content.
 *   2. An item is dropped when its content is unavailable: a `text` whose `field` — or, when
 *      it has none, whose `dropsWithField` — resolves to `null`/`''`; an `image` whose bytes
 *      are absent; a `qr` with no visit id. A `text` with a `literal` and no `dropsWithField`
 *      is never dropped.
 *   3. When an item is dropped, every later item with the same `flow` moves up by that
 *      item's `collapseHeightDots`. Items in another flow, or with `flow: null`, do not move.
 *   4. Spacing is never stretched to fill, and the group is never re-centred.
 */
const flowFields = {
    flow: z.string().min(1).nullable(),
    collapseHeightDots: z.number().int().nonnegative(),
};
export const badgeRectItemSchema = z
    .object({
    kind: z.literal('rect'),
    x: z.number().int(),
    y: z.number().int(),
    widthDots: z.number().int().positive(),
    heightDots: z.number().int().positive(),
    fill: z.enum(BADGE_FILL_ROLES),
    ...flowFields,
})
    .strict();
// A plain (non-refined) object schema, kept separate so it can be a member of the
// discriminatedUnion below — zod's discriminatedUnion requires ZodObject members, and
// .superRefine() would turn this into a ZodEffects that the union rejects at the type level.
// The field/literal mutual-exclusivity rule is applied twice: once here for anyone parsing a
// bare text item, and again on the union itself (see badgeDisplayItemSchema) so a caller
// parsing through the union gets the identical rejection.
const badgeTextItemObjectSchema = z
    .object({
    kind: z.literal('text'),
    /** The LEFT edge for LEFT align, the CENTRE for CENTER, the RIGHT edge for RIGHT. */
    x: z.number().int(),
    /** The TOP of the line box, not the baseline (SVG `dominant-baseline="hanging"`). */
    y: z.number().int(),
    sizeDots: z.number().int().positive(),
    lineHeightDots: z.number().int().positive(),
    weight: z.enum(BADGE_FONT_WEIGHTS),
    align: z.enum(BADGE_TEXT_ALIGNS),
    maxWidthDots: z.number().int().positive(),
    /** A per-visit value the drawer substitutes from local data. Mutually exclusive with `literal`. */
    field: z.enum(BADGE_SLOT_FIELDS).nullable(),
    /** Fixed text the server already resolved. Mutually exclusive with `field`. */
    literal: z.string().nullable(),
    /** Drawn in WHITE, for text sitting on a BLACK/ACCENT rect (a knockout block). */
    reversed: z.boolean(),
    /** Drawn in `BADGE_MUTED_INK` instead of black — the field-name captions. Ignored when
     *  `reversed` is true (a knockout caption is white, not grey). */
    muted: z.boolean(),
    /** Tracking, in dots, added after each glyph. 0 for ordinary text. NOT counted by
     *  `truncateToWidth`, deliberately: the ratio-based estimate is shared verbatim with the
     *  Dart drawer and the only text that carries tracking is a caption a dozen glyphs long
     *  in a column hundreds of dots wide. */
    letterSpacingDots: z.number().int().nonnegative(),
    /**
     * Ties this item's SURVIVAL to a field it does not itself print. `null` for ordinary
     * text, and then the drop rule is unchanged: a `field` item drops when its own value is
     * empty, a `literal` item never drops.
     *
     * The one use is a field-name caption: its text is a `literal` (the server already
     * resolved the i18n string), but it must vanish with the value it names — a badge that
     * prints "COMPANY" over empty space is worse than one that prints neither. So a caption
     * carries `literal` for WHAT to draw and `dropsWithField` for WHETHER to draw it, and
     * both it and its value drop together on the one flow group.
     *
     * NOT A VERSION BUMP. A drawer built before this field ignores it and prints an orphan
     * caption on the rare empty-value visit — cosmetic. Bumping `version` would make that
     * drawer refuse the whole list and strand a visitor at check-in, which this contract
     * already says it prefers to avoid ("prints a badge missing one element rather than
     * stranding the visitor").
     */
    dropsWithField: z.enum(BADGE_SLOT_FIELDS).nullable(),
    ...flowFields,
})
    .strict();
function checkTextItemSource(value, ctx) {
    if ((value.field === null) === (value.literal === null)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['field'],
            message: 'TEXT_ITEM_NEEDS_EXACTLY_ONE_SOURCE',
        });
    }
}
export const badgeTextItemSchema = badgeTextItemObjectSchema.superRefine(checkTextItemSource);
export const badgeImageItemSchema = z
    .object({
    kind: z.literal('image'),
    x: z.number().int(),
    y: z.number().int(),
    widthDots: z.number().int().positive(),
    heightDots: z.number().int().positive(),
    source: z.enum(BADGE_IMAGE_SOURCES),
    fit: z.enum(BADGE_IMAGE_FIT_MODES),
    ...flowFields,
})
    .strict();
export const badgeQrItemSchema = z
    .object({
    kind: z.literal('qr'),
    x: z.number().int(),
    y: z.number().int(),
    sizeDots: z.number().int().positive(),
    payload: z.enum(BADGE_QR_PAYLOAD_KINDS),
    ...flowFields,
})
    .strict();
export const badgeRuleItemSchema = z
    .object({
    kind: z.literal('rule'),
    x: z.number().int(),
    y: z.number().int(),
    widthDots: z.number().int().positive(),
    thicknessDots: z.number().int().positive(),
    fill: z.enum(BADGE_FILL_ROLES),
    ...flowFields,
})
    .strict();
export const badgeDisplayItemSchema = z
    .discriminatedUnion('kind', [
    badgeRectItemSchema,
    badgeTextItemObjectSchema,
    badgeImageItemSchema,
    badgeQrItemSchema,
    badgeRuleItemSchema,
])
    .superRefine((value, ctx) => {
    if (value.kind === 'text')
        checkTextItemSource(value, ctx);
});
/** Positions are ABSOLUTE canvas coordinates in printer dots, in the layout orientation's own
 *  canvas — never rotated. Rotation happens once, at the rasterization boundary
 *  (docs/plans/badge-printing.md §"Layer 2"); nothing in this contract knows about it.
 *
 *  `version` is not a "new kind" gate — it is bumped only if the meaning of an existing field
 *  changes. A drawer must accept a list whose `version` it knows and refuse one it does not. */
export const badgeDisplayListSchema = z
    .object({
    version: z.literal(BADGE_DISPLAY_LIST_VERSION),
    orientation: z.enum(BADGE_ORIENTATIONS),
    canvasWidthDots: z.number().int().positive(),
    canvasHeightDots: z.number().int().positive(),
    /** Draw order. Later items paint over earlier ones. */
    items: z.array(badgeDisplayItemSchema),
})
    .strict();
//# sourceMappingURL=badge-display-list.js.map